import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AdmBtn } from './AdminAtoms'
import { useBulletinExtract, useCreateEvent, type BulletinExtractedEvent } from '../../hooks/useChurchAdmin'
import { EVENT_TYPES } from '../ui/eventTypes'

const ACCEPTED = 'image/jpeg,image/png,image/webp'
const MAX_DIM = 2000 // keep large enough for readable text

async function prepareForExtraction(file: File): Promise<{ base64: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const { naturalWidth: w, naturalHeight: h } = img
      const scale = Math.min(1, MAX_DIM / Math.max(w, h))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      // Use JPEG at high quality — better for text OCR than WebP
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Image resize failed')); return }
          const reader = new FileReader()
          reader.onload = () => {
            const dataUrl = reader.result as string
            // Strip the data:image/jpeg;base64, prefix
            const base64 = dataUrl.split(',')[1]
            resolve({ base64, mediaType: 'image/jpeg' })
          }
          reader.onerror = () => reject(new Error('Could not read image'))
          reader.readAsDataURL(blob)
        },
        'image/jpeg',
        0.92,
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')) }
    img.src = url
  })
}

function formatEventDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

interface ExtractedEventRowProps {
  event: BulletinExtractedEvent
  selected: boolean
  onToggle: () => void
}

function ExtractedEventRow({ event, selected, onToggle }: ExtractedEventRowProps) {
  const t = EVENT_TYPES[event.eventType as keyof typeof EVENT_TYPES] ?? EVENT_TYPES.potluck

  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 12,
        cursor: 'pointer',
        border: `${selected ? 2 : 1}px solid ${selected ? t.color : 'var(--line-soft)'}`,
        background: selected ? t.tint : 'var(--surface)',
        transition: 'border-color 0.12s, background 0.12s',
        margin: selected ? '-1px 0' : 0,
      }}
    >
      <div
        style={{
          width: 18, height: 18, borderRadius: 4, marginTop: 2, flexShrink: 0,
          border: selected ? 'none' : '1.5px solid var(--line)',
          background: selected ? t.color : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {selected && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path d="M1 4L4 7.5L10 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: t.color,
          }}>
            {t.label}
          </span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>
          {event.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
          {formatEventDate(event.startsAt)}
          {event.endsAt && ` – ${formatEventDate(event.endsAt)}`}
        </div>
        {event.description && (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, lineClamp: 2 }}>
            {event.description}
          </div>
        )}
      </div>
    </div>
  )
}

interface Props {
  churchId: string
  churchSlug: string
  churchAddressText: string
  churchLocationLat: number
  churchLocationLng: number
  onClose: () => void
}

export function BulletinImportModal({ churchId, churchSlug, churchAddressText, churchLocationLat, churchLocationLng, onClose }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const [phase, setPhase] = useState<'pick' | 'extracting' | 'review' | 'importing' | 'done'>('pick')
  const [error, setError] = useState<string | null>(null)
  const [extracted, setExtracted] = useState<BulletinExtractedEvent[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [importedCount, setImportedCount] = useState(0)
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null)

  const queryClient = useQueryClient()
  const extractMutation = useBulletinExtract(churchId)
  const createEvent = useCreateEvent(churchSlug)

  async function handleFile(file: File) {
    setError(null)
    setPhase('extracting')
    try {
      const { base64, mediaType } = await prepareForExtraction(file)
      const result = await extractMutation.mutateAsync({ imageBase64: base64, mediaType })
      const events = result.data.events
      setUsage(result.data.usage)
      if (events.length === 0) {
        setError('No upcoming events found in this image. Try a clearer photo of the bulletin.')
        setPhase('pick')
        return
      }
      setExtracted(events)
      setSelected(new Set(events.map((_, i) => i)))
      setPhase('review')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Extraction failed'
      setError(msg)
      setPhase('pick')
    }
  }

  async function handleImport() {
    setPhase('importing')
    const toImport = extracted.filter((_, i) => selected.has(i))
    let created = 0

    for (const ev of toImport) {
      try {
        await createEvent.mutateAsync({
          churchId,
          title: ev.title,
          description: ev.description ?? undefined,
          eventType: ev.eventType,
          startsAt: ev.startsAt,
          endsAt: ev.endsAt ?? undefined,
          addressText: churchAddressText,
          locationLat: churchLocationLat,
          locationLng: churchLocationLng,
          visibility: 'public',
          status: 'draft',
          rsvpRequiresApproval: false,
          isRecurring: false,
          tags: [],
        })
        created++
      } catch {
        // skip failures — we'll report what got through
      }
    }

    void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'drafts'] })
    setImportedCount(created)
    setPhase('done')
  }

  function toggleAll() {
    if (selected.size === extracted.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(extracted.map((_, i) => i)))
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          width: '100%', maxWidth: 520,
          background: 'var(--surface)',
          borderRadius: '20px 20px 0 0',
          padding: '24px 20px 32px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Drag handle */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--line)', margin: '0 auto 20px' }} />

        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>
          Import from bulletin
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
          Upload a photo of your church bulletin or event flyer. Claude will extract the events for you to review.
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 16,
            background: '#FEF2F2', border: '1px solid #FECACA',
            fontSize: 13, color: '#DC2626',
          }}>
            {error}
          </div>
        )}

        {phase === 'pick' && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFile(file)
              }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                width: '100%', padding: '32px 16px',
                border: '2px dashed var(--line)',
                borderRadius: 14, cursor: 'pointer',
                background: 'var(--surface-2)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                fontFamily: 'inherit',
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                Choose image
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                JPEG, PNG or WebP — bulletin, flyer, or screenshot
              </span>
            </button>
          </>
        )}

        {phase === 'extracting' && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 14 }}>
            <div style={{ marginBottom: 12, fontSize: 28 }}>🔍</div>
            Reading your bulletin…
          </div>
        )}

        {phase === 'review' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                {extracted.length} event{extracted.length !== 1 ? 's' : ''} found
                {usage && (
                  <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 8 }}>
                    · {usage.used}/{usage.limit} imports used this month
                  </span>
                )}
              </div>
              <button
                onClick={toggleAll}
                style={{ fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
              >
                {selected.size === extracted.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {extracted.map((ev, i) => (
                <ExtractedEventRow
                  key={i}
                  event={ev}
                  selected={selected.has(i)}
                  onToggle={() => setSelected(prev => {
                    const next = new Set(prev)
                    next.has(i) ? next.delete(i) : next.add(i)
                    return next
                  })}
                />
              ))}
            </div>

            <AdmBtn
              variant="primary"
              block
              disabled={selected.size === 0}
              onClick={handleImport}
            >
              Import {selected.size} event{selected.size !== 1 ? 's' : ''}
            </AdmBtn>
          </>
        )}

        {phase === 'importing' && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 14 }}>
            <div style={{ marginBottom: 12, fontSize: 28 }}>⏳</div>
            Creating events…
          </div>
        )}

        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
              {importedCount} draft{importedCount !== 1 ? 's' : ''} saved
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>
              Review each one, make any corrections, and publish when ready.
            </div>
            <AdmBtn variant="primary" block onClick={() => {
              onClose()
              navigate(`/churches/${churchSlug}/events`)
            }}>
              Review drafts
            </AdmBtn>
          </div>
        )}
      </div>
    </div>
  )
}
